import React, { FC, useCallback, useEffect, useState } from 'react'
import { format, parseISO, startOfMonth, endOfMonth, subMonths, addMonths } from 'date-fns'
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerCloseButton,
  Button,
  Box,
  Checkbox,
  Flex,
  Text
} from '@chakra-ui/react'
import { IPropertiesProps } from '../../../interfaces/IPropertiesProps'
import { IHouseProps, IItemProps, IPriceHouseProps } from '../../../Hooks/Concierge'
import { SelectWithSearch } from '../../Form/SelectWithSearch'
import api from '../../../Services/api'
import { IUserProps } from '../../../interfaces/IUserProps'
import IOptionsProps from '../../../interfaces/IOptionsProps'
import { useForm } from 'react-hook-form'
import { DatePicker } from '../../Form/Datepicker'

interface IFilterScheduleModalProps {
  open: boolean
  setOpen: (state: boolean) => void
  houses: IPropertiesProps[]
  onSubmit: (data?: any) => Promise<void>
  onClose: () => void
  houseFiltered: string[]
  partnersFiltered: string[]
  upcomingSchedulesFiltered: boolean
  byCreatedAtFiltered: boolean
  startDate?: string
  endDate?: string
  byCheckInFiltered: boolean
  showNormalReservationsFiltered: boolean
  showSpecialReservationsFiltered: boolean
}

const FilterScheduleModal: FC<IFilterScheduleModalProps> = ({ 
  open, 
  setOpen, 
  houses, 
  onSubmit, 
  onClose, 
  houseFiltered,
  partnersFiltered,
  upcomingSchedulesFiltered,
  byCreatedAtFiltered,
  startDate,
  endDate,
  byCheckInFiltered,
  showNormalReservationsFiltered,
  showSpecialReservationsFiltered
}) => {
  const [tempItem, setTempItem] = useState<string[]>([])
  const [usersSelected, setUsersSelected] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [users, setUsers] = useState<IOptionsProps[]>([])
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [error, setError] = useState(false)
  const [upcomingSchedules, setUpcomingSchedules] = useState(false)
  const [byCreatedAt, setByCreatedAt] = useState(false)
  const [filterStartDate, setFilterStartDate] = useState<Date | null>(null)
  const [filterEndDate, setFilterEndDate] = useState<Date | null>(null)
  const [byCheckIn, setByCheckIn] = useState(false)
  const [showNormalReservations, setShowNormalReservations] = useState(true)
  const [showSpecialReservations, setShowSpecialReservations] = useState(true)
  const { register } = useForm()
  const onClick = useCallback(async () => {
    const startDateString = filterStartDate ? format(filterStartDate, 'yyyy-MM-dd') : ''
    const endDateString = filterEndDate ? format(filterEndDate, 'yyyy-MM-dd') : ''
    await onSubmit({ houses: tempItem, partners: usersSelected, upcomingSchedules, byCreatedAt, byCheckIn, startDate: startDateString, endDate: endDateString, showNormal: showNormalReservations, showSpecial: showSpecialReservations })
    onClose()

  }, [onSubmit, onClose, tempItem, usersSelected, upcomingSchedules, byCreatedAt, byCheckIn, filterStartDate, filterEndDate, showNormalReservations, showSpecialReservations])

  const handleSelectHouse = useCallback(
    (house: string) => {

      if(house === 'club') {
        setTempItem(['club'])
        return
      }

      if(house === 'homes') {
        setTempItem(['homes'])
        return
      }
      
      const filteredTempItem = tempItem.filter(i => i !== 'club' && i !== 'homes')

      const exists = filteredTempItem.find(i => i === house)

      if (exists != null) {
        setTempItem(filteredTempItem.filter(i => i !== house))
        return
      }

      setTempItem([...filteredTempItem, house])
    },
    [tempItem]
  )

  useEffect(() => {
    setTempItem(houseFiltered)
    setUsersSelected(partnersFiltered)
    setUpcomingSchedules(upcomingSchedulesFiltered)
    setByCreatedAt(byCreatedAtFiltered)
    setByCheckIn(byCheckInFiltered)
    setShowNormalReservations(showNormalReservationsFiltered)
    setShowSpecialReservations(showSpecialReservationsFiltered)
    // Se não há datas passadas como props, use o mês atual como padrão
    if (startDate && endDate) {
      setFilterStartDate(parseISO(startDate))
      setFilterEndDate(parseISO(endDate))
    } else if (!filterStartDate && !filterEndDate) {
      // Apenas define valores padrão se não há valores já definidos
      const now = new Date()
      setFilterStartDate(startOfMonth(subMonths(now, 1)))
      setFilterEndDate(endOfMonth(addMonths(now, 1)))
    }
  }, [houseFiltered, partnersFiltered, upcomingSchedulesFiltered, byCreatedAtFiltered, byCheckInFiltered, showNormalReservationsFiltered, showSpecialReservationsFiltered, startDate, endDate])

  const handleRemoveAll = useCallback(() => {
    setTempItem([])
  }, [])

  const handleAddAll = useCallback(() => {
    const allHouseIds = houses.map(house => house.id)
    setTempItem(allHouseIds)
  }, [houses])



  const loadUsers = useCallback(async () => {
    setLoading(true)
    try {
      
      const { data } = await api.get('/users', {
        params: {
          pag: 1,
          all: true,
          active: [0, 1]
        }
      })

      const normalize = data?.data?.map((i: any) => {
        return {
          value: i.id,
          label: i.name
        }
      })

      setUsers(normalize)
    } catch (error) {
      console.log('erro ao pegar users: ', error)
      setError(true)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadUsers()
  }, [])

  return (
    <Drawer isOpen={open} placement="right" onClose={() => setOpen(false)} size='md'>
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader>Agenda - Filtros</DrawerHeader>
        <DrawerBody>

          <Box mb='20px'>
            <Text fontSize='16px' fontWeight='600' color='green.dark' mb='10px'>
              Período
            </Text>
            <Flex gap='10px' direction={['column', 'row']}>
              <Box flex='1'>
                <DatePicker
                  name="startDate"
                  label="Data Inicial"
                  selected={filterStartDate}
                  onChange={(date: Date | null) => setFilterStartDate(date)}
                  placeholderText="Selecione a data inicial"
                  isClearable
                />
              </Box>
              <Box flex='1'>
                <DatePicker
                  name="endDate"
                  label="Data Final"
                  selected={filterEndDate}
                  onChange={(date: Date | null) => setFilterEndDate(date)}
                  placeholderText="Selecione a data final"
                  isClearable
                />
              </Box>
            </Flex>
          </Box>

                  <Box>
            <SelectWithSearch
                label="Sócio/Membro"
                title={'Selecionar Sócio/Membro'}
                options={users}
                defaultValues={usersSelected}
                extraFunction={(selectedValues: string[]) => {
                  setUsersSelected(selectedValues)
                }}
                onRemove={(removedValue: string | number) => {
                  setUsersSelected(prev => prev.filter(id => id !== removedValue.toString()))
                }}

                {...register('user')}

            />
          </Box>

          <Box mt='20px'>
            <Text fontSize='16px' fontWeight='600' color='green.dark' mb='10px'>
              Tipo de reserva
            </Text>
            <Flex direction='column' gap='5px'>
              <Checkbox
                color="green.dark"
                isChecked={showNormalReservations}
                onChange={(e) => setShowNormalReservations(e.target.checked)}
                style={{ padding: 5, color: '#000' }}
              >
                Mostrar reservas normais
              </Checkbox>
              <Checkbox
                color="green.dark"
                isChecked={showSpecialReservations}
                onChange={(e) => setShowSpecialReservations(e.target.checked)}
                style={{ padding: 5, color: '#000' }}
              >
                Mostrar reservas especiais
              </Checkbox>
            </Flex>
          </Box>

          <Box mt='20px'>
            <Checkbox
              color="green.dark"
              isChecked={upcomingSchedules}
              onChange={(e) => setUpcomingSchedules(e.target.checked)}
              style={{ padding: 5, color: '#000' }}
            >
              Agendamentos com check-in de hoje em diante
            </Checkbox>
          </Box>

          

          <Box mt='20px'> 
          <Text fontSize='16px' fontWeight='600' color='green.dark' mb='10px'>
              Ordenar por
            </Text>
            <Checkbox
              color="green.dark"
              isChecked={byCreatedAt}
              onChange={(e) => {
                if (e.target.checked) {
                  setByCreatedAt(true)
                  setByCheckIn(false)
                } else {
                  setByCreatedAt(false)
                }
              }}
              style={{ padding: 5, color: '#000' }}
            >
              Ordenar pelos mais recentes (data de criação)
            </Checkbox>
          </Box>

          <Box mt='20px'> 
            <Checkbox
              color="green.dark"
              isChecked={byCheckIn}
              onChange={(e) => {
                if (e.target.checked) {
                  setByCheckIn(true)
                  setByCreatedAt(false)
                } else {
                  setByCheckIn(false)
                }
              }}
              style={{ padding: 5, color: '#000' }}
            >
              Ordenar pelos mais recentes (check-in)
            </Checkbox>
          </Box>

          <Text mt='20px' fontSize='16px' fontWeight='600' color='green.dark'>
          Visualizar agendamentos de quais casas?
          </Text>


          <Flex mt='20px'>
            <Button variant="outline" mr={3} onClick={handleRemoveAll}>
              Remover Todos
            </Button>
            <Button variant="outline" mr={3} onClick={handleAddAll}>
              Selecionar Todos
            </Button>
          </Flex>

          <Box mt='20px'>
          <Checkbox
               
                color="green.dark"
                value='club'
                isChecked={tempItem?.includes('club')}
                name={`availability-house-club`}
                style={{ padding: 5, color: '#000' }}
                onChange={(e) => handleSelectHouse('club')}
            >
                Visualizar apenas as Casas do Club
            </Checkbox>

            <Checkbox
               
                color="green.dark"
                value='club'
                isChecked={tempItem?.includes('homes')}
                name={`availability-house-homes`}
                style={{ padding: 5, color: '#000' }}
                onChange={(e) => handleSelectHouse('homes')}
            >
                Visualizar apenas as Casas do Homes
            </Checkbox>
          {houses.map((house) => (
            <Box w='100%' key={house.id}>
            <Checkbox
                key={house.id}
                color="green.dark"
                value={house.id}
                isChecked={tempItem?.includes(house.id)}
                name={`availability-house-${house.id}`}
                style={{ padding: 5, color: '#000' }}
                onChange={(e) =>
                  handleSelectHouse(house.id)
                }
            >
                {house.name}
            </Checkbox>
            </Box>
          ))}
          </Box>

         
        </DrawerBody>
        <DrawerFooter>
          <Button variant="outline" mr={3} onClick={() => setOpen(false)}>
            Cancelar
          </Button>
          <Button backgroundColor='green.dark' color='white' onClick={onClick}>Aplicar Filtros</Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}

export { FilterScheduleModal }
